import React, { MouseEventHandler } from 'react';
import Router from 'next/router';

// theme
import { spacing, fontSizes, theme } from '../../utils';

// components
import { Spinner } from '../Spinner';
import { ArrowRightIcon, CheckIcon } from '../Icon';

const buttonSizeProps = {
  default: {
    fontSize: fontSizes['medium'],
    padding: `${spacing['xsmall']} ${spacing['medium']}`,
  },
  large: {
    padding: `${spacing['small']} ${spacing['large']}`,
  },
  small: {
    padding: `${spacing['xxsmall']} ${spacing['medium']}`,
  },
  mobile: {
    fontSize: fontSizes['xmedium'],
    padding: `${spacing['small']} ${spacing['large']}`,
  },
};

const getPropsByVariant = ({ variant, color, theme }) => {
  const colorInPalette = theme.palette[color];

  // default solid
  const defaultSolidVariantProps = {
    main: {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontWeight: theme.typography.fontWeight.bold,
      fill: theme.palette.common.white,
      outline: 'none',
      textTransform: 'inherit',
    },
    hover: {
      backgroundImage: theme.gradient.regular,
      boxShadow: theme.shadows.regular,
      outline: 'none',
    },
    disabled: {
      color: theme.palette.grey[500],
    },
  };
  // default outline
  const defaultOutlineVariantProps = {
    main: {
      border: `1px solid ${theme.palette.common.black}`,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      outline: 'none',
    },
    hover: {
      border: `1px solid ${theme.palette.common.black}`,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      outline: 'none',
    },
  };

  // solid with color
  const solidVariantPropsByPalette = colorInPalette && {
    main: {
      border: `1px solid ${colorInPalette.main}`,
      backgroundColor: colorInPalette.main,
      color: colorInPalette.contrastText,
      fill: theme.palette.common.black,
      textTransform: 'inherit',
    },
    hover: {
      backgroundImage: colorInPalette.gradient,
      outline: 'none',
    },
    disabled: {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.grey[500],
    },
  };

  // outline with color
  const outlineVariantPropsByPalette = colorInPalette && {
    main: {
      border: `1px solid ${colorInPalette.main}`,
      backgroundColor: theme.palette.common.white,
      color: colorInPalette.main,
      fill: theme.palette.common.black,
    },
    hover: {
      border: `1px solid ${colorInPalette.light}`,
      backgroundColor: theme.palette.common.white,
      color: colorInPalette.light,
    },
  };

  // finary +
  const solidVariantPlusProps = {
    main: {
      background: 'linear-gradient(261.89deg, #C79164 -2.23%, #F1C086 56.3%, #FFFFFF 127.83%)',
      color: theme.palette.grey[800],
    },
  };

  const variants = {
    outline: colorInPalette ? outlineVariantPropsByPalette : defaultOutlineVariantProps,
    solid: colorInPalette ? solidVariantPropsByPalette : defaultSolidVariantProps,
    plus: solidVariantPlusProps,
  };
  return variants[variant] || variants.solid;
};

type ButtonProps = {
  color?: string;
  size?: string;
  type?: any;
  variant?: string;
  icon?: string;
  disabled?: boolean;
  isLoading?: any;
  isSuccess?: boolean;
  onClick?: void | MouseEventHandler;
  selected?: boolean;
  to?: string;
  children?: any;
  round?: boolean;
};

export const Button: React.FC<ButtonProps> = ({
  color,
  size,
  type = 'button',
  variant,
  icon,
  disabled,
  isLoading,
  isSuccess,
  onClick = (e) => e,
  selected,
  to,
  children,
  round,
}) => {
  const fontSizeBySize = buttonSizeProps[size]?.fontSize;
  const paddingBySize = buttonSizeProps[size]?.padding;
  // success button state starter
  const isSuccessful = isLoading === 'succeeded' || isSuccess;
  const statusColor = isSuccess ? '#1FC090' : color;
  // const statusColor = "#1FC090";

  const propsByVariant = getPropsByVariant({ variant, theme, color: statusColor });
  const filColor = propsByVariant.main.fill;
  const styleButton = {
    cursor: 'pointer',
    textAlign: 'center',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    lineHeight: '1.6rem',
    width: '100%',
    opacity: (disabled && 0.7) || (selected && 0.7),
    transition: 'all 0.5s ease-in-out',
    padding: buttonSizeProps.default.padding,
    fontSize: buttonSizeProps.default.fontSize,
    borderRadius: round ? '48px' : theme.shape.borderRadius.sm,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    ...(propsByVariant && propsByVariant.main),
    ...(paddingBySize && { padding: paddingBySize }),
    ...(fontSizeBySize && { fontSize: fontSizeBySize }),
    '&:hover, &:focus': !disabled && {
      ...(propsByVariant && propsByVariant.hover),
    },
    '&:disabled': disabled && {
      cursor: 'default',
      ...(propsByVariant && propsByVariant.disabled),
    },
    '@media (max-width: 992px)': {
      fontSize: buttonSizeProps.mobile.fontSize,
      padding: buttonSizeProps.mobile.padding,
    },
  };
  return (
    <button
      style={{ ...styleButton }}
      disabled={disabled}
      type={type}
      onClick={(e) => {
        if (!disabled && to) {
          Router.push(to);
        } else {
          return onClick(e);
        }
      }}>
      {!isSuccessful && isLoading && <Spinner color={filColor} />}
      {isSuccessful ? (
        <span className="relative h-2" style={{ top: '-2px', left: '-5px' }}>
          <CheckIcon size="small" color="#1FC090" />
        </span>
      ) : null}
      {children}
      {icon && <ArrowRightIcon size="small" color={filColor} />}
    </button>
  );
};

export default Button;
